<template>
  <div class="clock-widget">
    <div class="cw-header">
      <font-awesome-icon icon="clock" class="cw-icon" />
      <p class="cw-title">Today's Attendance</p>
    </div>

    <div v-if="loading" class="cw-skeleton"></div>
    <div v-else>

      <!-- Status row -->
      <div class="cw-status-row">
        <span class="status-chip" :class="status ? statusClass(status.status) : 'chip--gray'">
          {{ status ? statusLabel(status.status) : 'Not yet clocked in' }}
        </span>
        <span class="cw-date">{{ todayLabel }}</span>
      </div>

      <!-- Outside-location warning badge (shown after clock-in with bypass) -->
      <div v-if="status && status.is_outside_location" class="cw-location-warn">
        <font-awesome-icon icon="exclamation-triangle" />
        Clocked in outside designated location
        <span v-if="status.location_bypass_reason" class="cw-bypass-reason">
          — {{ status.location_bypass_reason }}
        </span>
      </div>

      <!-- Times -->
      <div class="cw-times" v-if="status && (status.clock_in || status.clock_out)">
        <div class="cw-time-block">
          <span class="cw-time-label">Clock In</span>
          <span class="cw-time-val">{{ fmtTime(status.clock_in) || '—' }}</span>
        </div>
        <div class="cw-time-divider">→</div>
        <div class="cw-time-block">
          <span class="cw-time-label">Clock Out</span>
          <span class="cw-time-val">{{ fmtTime(status.clock_out) || '—' }}</span>
        </div>
        <div class="cw-time-block" v-if="status.worked_hours != null">
          <span class="cw-time-label">Hours</span>
          <span class="cw-time-val">{{ status.worked_hours }}h</span>
        </div>
      </div>

      <!-- Live clock -->
      <div class="cw-live-clock">{{ liveTime }}</div>

      <!-- Window not open yet -->
      <div v-if="canClockIn && !clockWindowOpen" class="cw-window-notice">
        <font-awesome-icon icon="clock" />
        Clock-in opens at {{ clockWindowOpensAt }} (30 min before shift)
      </div>

      <!-- Early clock-in reason -->
      <div v-if="canClockIn && clockWindowOpen && isEarlyClockIn" class="cw-reason-block">
        <label class="cw-reason-label">
          Reason for early clock-in <span class="req">*</span>
          <span class="cw-reason-hint">Shift starts at {{ shiftStartLabel }}</span>
        </label>
        <textarea class="cw-reason-input" v-model="earlyClockInReason" rows="2"
          placeholder="Why are you clocking in early?"></textarea>
      </div>

      <!-- Early / late clock-out reason -->
      <div v-if="canClockOut && isEarlyOrLateClockOut" class="cw-reason-block">
        <label class="cw-reason-label">
          Reason for {{ clockoutType === 'early_clockout' ? 'early' : 'late' }} clock-out <span class="req">*</span>
          <span class="cw-reason-hint">Shift ends at {{ shiftEndLabel }}</span>
        </label>
        <textarea class="cw-reason-input" v-model="clockoutReason" rows="2"
          placeholder="Why are you clocking out at this time?"></textarea>
      </div>

      <!-- Action buttons -->
      <div class="cw-actions">
        <button v-if="canClockIn" class="cw-btn cw-btn--in"
          :disabled="acting || !clockWindowOpen || (isEarlyClockIn && earlyClockInReason.trim().length < 5)"
          @click="clockIn">
          <font-awesome-icon :icon="acting ? 'spinner' : 'sign-in-alt'" :spin="acting" />
          {{ acting ? 'Clocking in…' : 'Clock In' }}
        </button>
        <button v-if="canClockOut" class="cw-btn cw-btn--out"
          :disabled="acting || (isEarlyOrLateClockOut && clockoutReason.trim().length < 5)"
          @click="clockOut">
          <font-awesome-icon :icon="acting ? 'spinner' : 'sign-out-alt'" :spin="acting" />
          {{ acting ? 'Clocking out…' : 'Clock Out' }}
        </button>
        <div v-if="!canClockIn && !canClockOut && status" class="cw-done">
          <font-awesome-icon icon="check-circle" /> Day complete
        </div>
      </div>

      <!-- GPS note -->
      <p class="cw-gps-note" v-if="locationError">{{ locationError }}</p>
    </div>

    <!-- ══ Location Bypass Reason Modal ══ -->
    <div v-if="reasonModal.show" class="lm-overlay" @click.self="reasonModal.show = false">
      <div class="lm-box">
        <div class="lm-head">
          <div class="lm-head-icon">
            <font-awesome-icon icon="map-marker-alt" />
          </div>
          <div>
            <p class="lm-title">Outside Designated Location</p>
            <p class="lm-sub">You need to provide a reason to clock in from here</p>
          </div>
        </div>

        <!-- Distance card -->
        <div class="lm-distance-card">
          <div class="lm-dist-row">
            <font-awesome-icon icon="building" class="lm-dist-icon" />
            <div>
              <p class="lm-dist-label">Client site</p>
              <p class="lm-dist-name">{{ reasonModal.locationInfo.client_name }}</p>
              <p class="lm-dist-addr" v-if="reasonModal.locationInfo.client_address">
                {{ reasonModal.locationInfo.client_address }}
              </p>
            </div>
          </div>
          <div class="lm-dist-badge">
            <font-awesome-icon icon="route" />
            <span>
              <strong>{{ reasonModal.locationInfo.distance_meters }}m</strong> away
              &nbsp;·&nbsp; allowed radius: {{ reasonModal.locationInfo.geofence_radius }}m
            </span>
          </div>
        </div>

        <!-- Reason input -->
        <div class="lm-body">
          <label class="lm-label">Reason for clocking in outside the designated location <span class="req">*</span></label>
          <textarea
            class="lm-textarea"
            v-model="reasonModal.reason"
            placeholder="e.g. Working from the client's head office today, temporary reassignment…"
            rows="4"
            maxlength="500"
          ></textarea>
          <p class="lm-char-count">{{ reasonModal.reason.length }}/500</p>
        </div>

        <div class="lm-footer">
          <button class="lm-btn-cancel" @click="reasonModal.show = false">Cancel</button>
          <button
            class="lm-btn-confirm"
            :disabled="reasonModal.reason.trim().length < 5 || reasonModal.submitting"
            @click="clockInWithReason"
          >
            <font-awesome-icon :icon="reasonModal.submitting ? 'spinner' : 'sign-in-alt'" :spin="reasonModal.submitting" />
            {{ reasonModal.submitting ? 'Clocking in…' : 'Clock In Anyway' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ClockWidget',
  props: {
    employeeId: { type: String, default: null },
  },
  data() {
    return {
      loading:              false,
      acting:               false,
      status:               null,
      todayShift:           null,   // today's shift for time-window enforcement
      noShiftClock:         false,  // no shifts at all — skip GPS/location
      clockWindowOpen:      true,   // false until 30 min before shift start
      clockWindowOpensAt:   '',     // "09:30" display label
      liveTime:             '',
      locationError:        '',
      clockTimer:           null,
      earlyClockInReason:   '',
      clockoutReason:       '',
      // Captured coords used across normal + bypass flows
      pendingCoords: null,
      reasonModal: {
        show:         false,
        reason:       '',
        submitting:   false,
        locationInfo: null,
      },
    };
  },
  computed: {
    canClockIn()  { return !this.status || !this.status.clock_in; },
    canClockOut() { return this.status && this.status.clock_in && !this.status.clock_out; },
    todayLabel() {
      return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
    isEarlyClockIn() {
      if (!this.todayShift || this.noShiftClock || !this.canClockIn) return false;
      const [h, m] = (this.todayShift.start_time || '').split(':');
      if (h === undefined) return false;
      const shiftStart = new Date(); shiftStart.setHours(+h, +m, 0, 0);
      return new Date() < shiftStart;
    },
    isEarlyOrLateClockOut() {
      if (!this.todayShift || this.noShiftClock || !this.canClockOut) return false;
      const [h, m] = (this.todayShift.end_time || '').split(':');
      if (h === undefined) return false;
      const shiftEnd = new Date(); shiftEnd.setHours(+h, +m, 0, 0);
      const now = new Date();
      return now < new Date(shiftEnd.getTime() - 15 * 60000) || now > shiftEnd;
    },
    clockoutType() {
      if (!this.todayShift || !this.isEarlyOrLateClockOut) return '';
      const [h, m] = (this.todayShift.end_time || '').split(':');
      const shiftEnd = new Date(); shiftEnd.setHours(+h, +m, 0, 0);
      return new Date() < shiftEnd ? 'early_clockout' : 'late_clockout';
    },
    shiftStartLabel()  { return this.fmtHM(this.todayShift?.start_time); },
    shiftEndLabel()    { return this.fmtHM(this.todayShift?.end_time); },
  },
  mounted() {
    this.fetchStatus();
    this.startClock();
  },
  beforeUnmount() { clearInterval(this.clockTimer); },
  methods: {

    async fetchStatus() {
      this.loading = true;
      try {
        const params = this.employeeId ? { employee_id: this.employeeId } : {};
        const { data } = await axios.get('/hr/attendance/today-status', { params });
        this.status       = data.data || null;
        this.noShiftClock = data.data?.no_shift_clock ?? false;
        this.todayShift   = data.data?.today_shift ?? null;
        this.updateShiftWindow();
      } catch { this.toast.error('Failed to load status'); }
      finally { this.loading = false; }
    },

    async clockIn() {
      this.acting = true;
      this.locationError = '';
      // Declared outside try so catch block can reference pendingCoords without scope error
      if (!this.noShiftClock) {
        this.pendingCoords = await this.getCoords();
      }
      try {
        await axios.post('/hr/attendance/clock-in', {
          employee_id:          this.employeeId || undefined,
          latitude:             this.pendingCoords?.lat ?? null,
          longitude:            this.pendingCoords?.lng ?? null,
          early_clockin_reason: this.earlyClockInReason.trim() || undefined,
        });
        this.$toast?.success('Clocked in!');
        this.earlyClockInReason = '';
        this.fetchStatus();
      } catch (e) {
        if (e.response?.status === 425) {
          this.$toast?.error(e.response?.data?.message || 'Clock-in not available yet');
        } else if (e.response?.status === 428) {
          const type = e.response.data?.data?.type;
          if (!type || type === 'geofence') {
            this.reasonModal = {
              show:         true,
              reason:       '',
              submitting:   false,
              locationInfo: e.response.data.data,
            };
          } else {
            this.$toast?.error(e.response?.data?.message || 'Please provide a reason');
          }
        } else {
          this.$toast?.error(e.response?.data?.message || 'Clock-in failed');
        }
      } finally { this.acting = false; }
    },

    async clockInWithReason() {
      if (this.reasonModal.reason.trim().length < 5) return;
      this.reasonModal.submitting = true;
      try {
        await axios.post('/hr/attendance/clock-in', {
          employee_id:             this.employeeId || undefined,
          latitude:                this.pendingCoords?.lat,
          longitude:               this.pendingCoords?.lng,
          bypass_location_check:   true,
          location_bypass_reason:  this.reasonModal.reason.trim(),
        });
        this.reasonModal.show = false;
        this.$toast?.success('Clocked in with location note recorded');
        this.fetchStatus();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Clock-in failed');
      } finally {
        this.reasonModal.submitting = false;
      }
    },

    async clockOut() {
      this.acting = true;
      this.locationError = '';
      try {
        const coords = this.noShiftClock ? null : await this.getCoords();
        await axios.post('/hr/attendance/clock-out', {
          employee_id:           this.employeeId || undefined,
          latitude:              coords?.lat ?? null,
          longitude:             coords?.lng ?? null,
          early_clockout_reason: this.clockoutReason.trim() || undefined,
        });
        this.$toast?.success('Clocked out!');
        this.clockoutReason = '';
        this.fetchStatus();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Clock-out failed');
      } finally { this.acting = false; }
    },

    getCoords() {
      return new Promise((resolve) => {
        if (!navigator.geolocation) { resolve(null); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          ()    => { this.locationError = 'Location unavailable — GPS not recorded.'; resolve(null); },
          { timeout: 5000 }
        );
      });
    },

    startClock() {
      const tick = () => {
        this.liveTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        this.updateShiftWindow();
      };
      tick();
      this.clockTimer = setInterval(tick, 1000);
    },

    updateShiftWindow() {
      if (!this.todayShift || this.noShiftClock || !this.canClockIn) {
        this.clockWindowOpen = true;
        return;
      }
      const [h, m] = (this.todayShift.start_time || '00:00').split(':');
      const shiftStart = new Date(); shiftStart.setHours(+h, +m, 0, 0);
      const windowTime = new Date(shiftStart.getTime() - 30 * 60000);
      this.clockWindowOpen    = new Date() >= windowTime;
      this.clockWindowOpensAt = windowTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    fmtHM(t) {
      if (!t) return '';
      const [h, m] = t.split(':');
      const d = new Date(); d.setHours(+h, +m, 0, 0);
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    fmtTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    statusLabel(s) {
      return ({ present: 'Present', late: 'Late', absent: 'Absent', early_departure: 'Early Out', half_day: 'Half Day', on_leave: 'On Leave', holiday: 'Holiday' })[s] || s;
    },
    statusClass(s) {
      return { present: 'chip--green', late: 'chip--amber', absent: 'chip--red', early_departure: 'chip--orange', half_day: 'chip--purple', on_leave: 'chip--blue', holiday: 'chip--gray' }[s] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
/* ── Widget shell ──────────────────────────────────────────────────── */
.clock-widget { background:#fff; border:1px solid #e4eaf2; border-radius:16px; padding:20px; display:flex; flex-direction:column; gap:16px; }

.cw-header  { display:flex; align-items:center; gap:10px; }
.cw-icon    { color:#004AAD; font-size:18px; }
.cw-title   { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }

.cw-skeleton { height:160px; border-radius:10px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Status row ────────────────────────────────────────────────────── */
.cw-status-row { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:6px; }
.cw-date       { font-size:12px; color:#7a90b0; }

.status-chip  { display:inline-block; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:700; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--orange { background:#ffedd5; color:#ea580c; }
.chip--purple { background:#f3e8ff; color:#7c3aed; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

/* ── Outside-location warning ──────────────────────────────────────── */
.cw-location-warn {
  display:flex; align-items:flex-start; flex-wrap:wrap; gap:6px;
  padding:8px 12px; border-radius:8px;
  background:#fff7ed; border:1px solid #fdba74;
  font-size:12px; font-weight:600; color:#ea580c;
}
.cw-bypass-reason { font-weight:400; color:#9a3412; }

/* ── Times ─────────────────────────────────────────────────────────── */
.cw-times       { display:flex; align-items:center; gap:10px; background:#f8fafc; border-radius:10px; padding:12px 14px; }
.cw-time-block  { display:flex; flex-direction:column; gap:2px; flex:1; }
.cw-time-label  { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.cw-time-val    { font-size:16px; font-weight:800; color:#1a2b45; }
.cw-time-divider { color:#aab8cc; font-size:16px; }

.cw-live-clock { text-align:center; font-size:28px; font-weight:800; color:#1a2b45; letter-spacing:2px; font-variant-numeric:tabular-nums; font-family:monospace; }

/* ── Action buttons ─────────────────────────────────────────────────── */
.cw-actions { display:flex; gap:10px; }
.cw-btn     { flex:1; display:flex; align-items:center; justify-content:center; gap:8px; height:44px; border-radius:10px; font-size:14px; font-weight:700; cursor:pointer; border:none; transition:opacity .15s; }
.cw-btn:disabled { opacity:.6; cursor:not-allowed; }
.cw-btn--in  { background:#004AAD; color:#fff; }
.cw-btn--in:hover:not(:disabled)  { background:#003b8e; }
.cw-btn--out { background:#fee2e2; color:#dc2626; }
.cw-btn--out:hover:not(:disabled) { background:#fecaca; }

.cw-done     { flex:1; display:flex; align-items:center; justify-content:center; gap:6px; color:#16a34a; font-size:13px; font-weight:600; }
.cw-gps-note { font-size:11px; color:#ea580c; margin:0; text-align:center; }

/* ── Shift window notice ────────────────────────────────────────────── */
.cw-window-notice {
  display:flex; align-items:center; gap:8px;
  padding:10px 14px; border-radius:8px;
  background:#eff6ff; border:1px solid #bfdbfe;
  font-size:12px; font-weight:600; color:#1d4ed8;
}

/* ── Inline reason blocks ───────────────────────────────────────────── */
.cw-reason-block { display:flex; flex-direction:column; gap:5px; }
.cw-reason-label { font-size:11px; font-weight:700; color:#4a5e78; display:flex; flex-direction:column; gap:2px; }
.cw-reason-hint  { font-size:10px; font-weight:400; color:#7a90b0; }
.cw-reason-input {
  width:100%; padding:8px 10px; border:1px solid #dde5f0; border-radius:7px;
  font-size:12px; color:#1a2b45; resize:none; outline:none; box-sizing:border-box;
  font-family:inherit; line-height:1.5;
}
.cw-reason-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }

/* ══ Location bypass reason modal ══════════════════════════════════ */
.lm-overlay {
  position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:2000;
  display:flex; align-items:center; justify-content:center; padding:16px;
}
.lm-box {
  background:#fff; border-radius:16px; width:100%; max-width:480px;
  box-shadow:0 24px 60px rgba(0,0,0,.22); overflow:hidden;
}

.lm-head {
  display:flex; align-items:flex-start; gap:14px;
  padding:20px 22px 18px; border-bottom:1px solid #f0f4f8;
}
.lm-head-icon {
  width:42px; height:42px; border-radius:10px; flex-shrink:0;
  background:#fff7ed; color:#ea580c;
  display:flex; align-items:center; justify-content:center; font-size:18px;
}
.lm-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.lm-sub   { font-size:12px; color:#7a90b0; margin:0; }

.lm-distance-card {
  margin:16px 22px 0;
  border:1px solid #e4eaf2; border-radius:10px; overflow:hidden;
}
.lm-dist-row {
  display:flex; align-items:flex-start; gap:12px;
  padding:14px 16px; background:#f8fafc;
}
.lm-dist-icon  { font-size:18px; color:#004AAD; margin-top:2px; flex-shrink:0; }
.lm-dist-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin:0 0 2px; }
.lm-dist-name  { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.lm-dist-addr  { font-size:11px; color:#7a90b0; margin:0; }

.lm-dist-badge {
  display:flex; align-items:center; gap:8px;
  padding:10px 16px; background:#fff3cd; border-top:1px solid #fde68a;
  font-size:12px; color:#92400e;
}

.lm-body { padding:18px 22px 0; display:flex; flex-direction:column; gap:6px; }
.lm-label { font-size:12px; font-weight:600; color:#4a5e78; }
.lm-textarea {
  width:100%; padding:10px 12px; border:1px solid #dde5f0; border-radius:8px;
  font-size:13px; color:#1a2b45; resize:vertical; outline:none; box-sizing:border-box;
  font-family:inherit; line-height:1.5;
}
.lm-textarea:focus { border-color:#004AAD; }
.lm-char-count { font-size:11px; color:#aab8cc; margin:0; text-align:right; }
.req { color:#dc2626; }

.lm-footer {
  display:flex; justify-content:flex-end; gap:8px;
  padding:16px 22px; border-top:1px solid #f0f4f8; margin-top:16px;
}
.lm-btn-cancel {
  display:inline-flex; align-items:center; height:38px; padding:0 16px;
  border-radius:8px; border:1px solid #dde5f0; background:#fff;
  font-size:13px; font-weight:600; color:#1a2b45; cursor:pointer;
}
.lm-btn-cancel:hover { background:#f5f7fa; }
.lm-btn-confirm {
  display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 18px;
  border-radius:8px; border:none; background:#ea580c;
  font-size:13px; font-weight:600; color:#fff; cursor:pointer;
  transition:background .15s;
}
.lm-btn-confirm:hover:not(:disabled) { background:#c2410c; }
.lm-btn-confirm:disabled { opacity:.55; cursor:not-allowed; }
</style>
